import { useMemo, useRef, useState, useEffect } from "react";
import * as d3 from "d3";
import { PlotControls } from "@/components/VivoPlots/components/PlotControls";

export interface LineChartConfig {
  xField: string;
  yField: string;
  seriesField?: string;
  xIsDate?: boolean;
  xLabel?: string;
  yLabel?: string;
  title?: string;
  plotId?: string;
  plotClassName?: string;
  palette?: string;
}

interface LineChartVisualizationProps {
  rows: Record<string, unknown>[];
  config: {
    lineChart?: LineChartConfig;
    plotId?: string;
    plotClassName?: string;
    labels?: { title?: string };
  };
  isLoading: boolean;
  deletePlot?: (plotId: string) => void;
  hidePlot?: (plotId: string) => void;
  editPlot?: (plotInfo: any) => void;
}

const LINE_COLORS = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#fadb14",
  "#f5222d",
  "#a0d911",
  "#fa541c",
];

const MARGIN = { top: 20, right: 24, bottom: 48, left: 52 };

export const LineChartVisualization = ({
  rows,
  config,
  isLoading,
  deletePlot,
  hidePlot,
  editPlot,
}: LineChartVisualizationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 600, height: 320 });
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    label: string;
    value: string;
  } | null>(null);

  const lc = config.lineChart;

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDims({ width: Math.max(width, 200), height: Math.max(height, 120) });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const { series, xScale, yScale, colorScale } = useMemo(() => {
    if (!lc || !rows?.length)
      return { series: [], xScale: null, yScale: null, colorScale: null };

    const { xField, yField, seriesField, xIsDate } = lc;
    const innerW = dims.width - MARGIN.left - MARGIN.right;
    const innerH = dims.height - MARGIN.top - MARGIN.bottom;

    const parseX = xIsDate
      ? (v: unknown) => {
          if (typeof v === "number") {
            // YYYYMM integer → Date
            const s = String(v);
            if (s.length === 6)
              return new Date(+s.slice(0, 4), +s.slice(4) - 1);
          }
          return new Date(v as string);
        }
      : (v: unknown) => +v!;

    const groups = seriesField
      ? d3.group(rows, (d) => String(d[seriesField] ?? ""))
      : new Map([["", rows]]);

    const seriesKeys = [...groups.keys()].sort();

    const allX = rows
      .map((d) => parseX(d[xField]))
      .filter((v) => v != null && !isNaN(v as number)) as (Date | number)[];
    const allY = rows.map((d) => +d[yField]!).filter((v) => !isNaN(v));

    if (!allX.length || !allY.length)
      return { series: [], xScale: null, yScale: null, colorScale: null };

    const xDomain = d3.extent(allX) as [Date | number, Date | number];
    const yMax = d3.max(allY) ?? 0;

    const xs = xIsDate
      ? d3
          .scaleTime()
          .domain(xDomain as [Date, Date])
          .range([0, innerW])
      : d3
          .scaleLinear()
          .domain(xDomain as [number, number])
          .range([0, innerW]);

    const ys = d3
      .scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerH, 0])
      .nice();

    const cs = d3.scaleOrdinal<string>().domain(seriesKeys).range(LINE_COLORS);

    const seriesData = seriesKeys
      .map((key) => {
        const pts = (groups.get(key) ?? [])
          .map((d) => ({ x: parseX(d[xField]), y: +d[yField]! }))
          .filter((p) => p.x != null && !isNaN(p.x as number) && !isNaN(p.y))
          .sort((a, b) => (a.x as any) - (b.x as any));
        return { key, pts };
      })
      .filter((s) => s.pts.length > 0);

    return { series: seriesData, xScale: xs, yScale: ys, colorScale: cs };
  }, [rows, lc, dims]);

  if (!lc) {
    return (
      <div className="flex items-center justify-center w-full h-full p-4 text-gray-400 text-sm">
        No line chart configured.
      </div>
    );
  }

  const innerW = dims.width - MARGIN.left - MARGIN.right;
  const innerH = dims.height - MARGIN.top - MARGIN.bottom;

  const lineGen =
    xScale && yScale
      ? d3
          .line<{ x: Date | number; y: number }>()
          .x((d) => xScale(d.x as any))
          .y((d) => yScale(d.y))
          .curve(d3.curveMonotoneX)
      : null;

  const xTicks = xScale?.ticks(6) ?? [];
  const yTicks = yScale?.ticks(5) ?? [];

  const editThisPlot = () => {
    editPlot?.({ type: "line", config, dataIsLoading: false, data: rows });
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[200px] bg-white rounded-lg overflow-hidden"
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <span className="text-sm text-gray-400">Loading…</span>
        </div>
      )}

      {(deletePlot || hidePlot || editPlot) && (
        <div className="absolute top-2 right-2 z-10">
          <PlotControls
            inModal={false}
            showLegend={false}
            setShowLegend={() => {}}
            fillScale={null}
            editThisPlot={editThisPlot}
            hidePlot={hidePlot ?? (() => {})}
            deletePlot={deletePlot ?? (() => {})}
            plotClassName={config.plotClassName ?? ""}
            config={config as any}
          />
        </div>
      )}

      {config.labels?.title && (
        <p className="absolute top-2 left-3 text-xs font-semibold text-gray-600 truncate max-w-[60%]">
          {config.labels.title}
        </p>
      )}

      <svg
        width={dims.width}
        height={dims.height}
        className="block"
        onMouseLeave={() => setTooltip(null)}
      >
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {/* Y grid */}
          {yScale &&
            yTicks.map((tick) => (
              <line
                key={tick}
                x1={0}
                x2={innerW}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
            ))}

          {/* X axis */}
          {xScale && (
            <g transform={`translate(0,${innerH})`}>
              <line x1={0} x2={innerW} stroke="#9ca3af" />
              {xTicks.map((tick, i) => (
                <g key={i} transform={`translate(${xScale(tick as any)},0)`}>
                  <line y2={4} stroke="#9ca3af" />
                  <text y={16} textAnchor="middle" fontSize={10} fill="#6b7280">
                    {lc.xIsDate
                      ? d3.timeFormat("%b %Y")(tick as Date)
                      : String(tick)}
                  </text>
                </g>
              ))}
              {lc.xLabel && (
                <text
                  x={innerW / 2}
                  y={38}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#374151"
                >
                  {lc.xLabel}
                </text>
              )}
            </g>
          )}

          {/* Y axis */}
          {yScale && (
            <g>
              <line y1={0} y2={innerH} stroke="#9ca3af" />
              {yTicks.map((tick, i) => (
                <g key={i} transform={`translate(0,${yScale(tick)})`}>
                  <line x2={-4} stroke="#9ca3af" />
                  <text
                    x={-8}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={10}
                    fill="#6b7280"
                  >
                    {tick}
                  </text>
                </g>
              ))}
              {lc.yLabel && (
                <text
                  transform={`translate(-40,${innerH / 2}) rotate(-90)`}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#374151"
                >
                  {lc.yLabel}
                </text>
              )}
            </g>
          )}

          {/* Lines */}
          {lineGen &&
            series.map(({ key, pts }) => (
              <path
                key={key}
                d={lineGen(pts) ?? ""}
                fill="none"
                stroke={colorScale?.(key) ?? LINE_COLORS[0]}
                strokeWidth={2}
              />
            ))}

          {/* Dots with tooltip */}
          {xScale &&
            yScale &&
            series.map(({ key, pts }) =>
              pts.map((p, i) => (
                <circle
                  key={`${key}-${i}`}
                  cx={xScale(p.x as any)}
                  cy={yScale(p.y)}
                  r={3}
                  fill={colorScale?.(key) ?? LINE_COLORS[0]}
                  stroke="white"
                  strokeWidth={1}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    const svg = (e.target as SVGCircleElement)
                      .closest("svg")!
                      .getBoundingClientRect();
                    setTooltip({
                      x: e.clientX - svg.left,
                      y: e.clientY - svg.top - 8,
                      label: key ? `${key}: ` : "",
                      value: String(p.y),
                    });
                  }}
                />
              )),
            )}
        </g>
      </svg>

      {/* Legend */}
      {series.length > 1 && (
        <div className="absolute bottom-1 left-0 right-0 flex flex-wrap justify-center gap-x-3 gap-y-1 px-2">
          {series.slice(0, 10).map(({ key }) => (
            <span
              key={key}
              className="flex items-center gap-1 text-xs text-gray-600"
            >
              <span
                className="inline-block w-3 h-[2px]"
                style={{ background: colorScale?.(key) ?? LINE_COLORS[0] }}
              />
              {key || "(none)"}
            </span>
          ))}
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-20"
          style={{ left: tooltip.x + 8, top: tooltip.y }}
        >
          {tooltip.label}
          {tooltip.value}
        </div>
      )}
    </div>
  );
};
